import React, { useRef } from "react";
import { motion } from "framer-motion";
import {
  ArrowRight,
  ChevronLeft,
  ChevronRight,
  Calendar as CalendarIcon,
} from "lucide-react";
import { getAllPosts } from "@/blog/posts";

function formatDate(d) {
  try {
    return new Date(d + "T12:00:00").toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return d;
  }
}

export function LatestPosts() {
  const posts = getAllPosts();
  const scrollerRef = useRef(null);

  if (posts.length === 0) return null;

  const scrollBy = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    // Scroll by roughly one card width
    const firstCard = el.querySelector("[data-blog-card]");
    const cardWidth = firstCard ? firstCard.getBoundingClientRect().width + 24 : el.clientWidth * 0.9;
    el.scrollBy({ left: dir * cardWidth, behavior: "smooth" });
  };

  return (
    <section
      id="blog"
      className="bg-[#F7F7F5] py-20 border-t border-[#E5E5E5]"
      data-testid="home-blog-section"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.22em] text-[#5FBE1F] mb-4">
              <span className="w-6 h-px bg-[#5FBE1F]" />
              From the truck
            </div>
            <h2 className="font-display text-4xl sm:text-5xl text-[#000000] leading-tight">
              Latest from the <span className="text-[#5FBE1F]">blog</span>
            </h2>
            <p className="mt-3 text-[#4A4A46] text-base sm:text-lg">
              Straight-talk gutter and home maintenance tips for southwest Iowa homeowners.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => scrollBy(-1)}
              aria-label="Previous post"
              className="w-10 h-10 rounded-sm border border-[#0A0A0A]/20 bg-white text-[#0A0A0A] flex items-center justify-center hover:bg-[#0A0A0A] hover:text-white hover:border-[#0A0A0A] transition-colors"
              data-testid="home-blog-prev"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              type="button"
              onClick={() => scrollBy(1)}
              aria-label="Next post"
              className="w-10 h-10 rounded-sm border border-[#0A0A0A]/20 bg-white text-[#0A0A0A] flex items-center justify-center hover:bg-[#0A0A0A] hover:text-white hover:border-[#0A0A0A] transition-colors"
              data-testid="home-blog-next"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
            <a
              href="/blog"
              className="hidden md:inline-flex items-center gap-2 ml-2 text-xs font-bold uppercase tracking-[0.18em] text-[#0A0A0A] hover:text-[#5FBE1F]"
              data-testid="home-blog-view-all"
            >
              View all posts <ArrowRight className="w-3.5 h-3.5" />
            </a>
          </div>
        </div>

        <div
          ref={scrollerRef}
          className="flex gap-6 overflow-x-auto snap-x snap-mandatory pb-4 -mx-6 px-6 lg:-mx-8 lg:px-8 scrollbar-hide"
          style={{ scrollbarWidth: "none" }}
          data-testid="home-blog-carousel"
        >
          {posts.map((post, i) => (
            <motion.article
              key={post.slug}
              data-blog-card
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5, delay: i * 0.05 }}
              className="group bg-white border border-[#E5E5E5] rounded-sm overflow-hidden flex flex-col hover:border-[#5FBE1F] transition-all flex-shrink-0 snap-start w-[85%] sm:w-[60%] md:w-[calc(50%-12px)] lg:w-[calc(33.333%-16px)]"
              data-testid={`home-blog-card-${post.slug}`}
            >
              <a href={`/blog/${post.slug}`} className="block">
                <div
                  className="aspect-[2/1] bg-[#F5F5F5] bg-cover bg-center"
                  style={{ backgroundImage: `url('${post.cover_image}')` }}
                  role="img"
                  aria-label={post.cover_alt || post.title}
                />
              </a>
              <div className="p-6 flex-1 flex flex-col">
                <div className="flex items-center gap-2 text-xs text-[#4A4A46] mb-3">
                  <CalendarIcon className="w-3.5 h-3.5" />
                  <time dateTime={post.date}>{formatDate(post.date)}</time>
                  {post.tags && post.tags.length > 0 && (
                    <>
                      <span className="text-[#9CA09B]">·</span>
                      <span className="uppercase tracking-[0.18em] text-[#5FBE1F] font-bold text-[10px]">
                        {post.tags[0]}
                      </span>
                    </>
                  )}
                </div>
                <h3 className="font-display text-xl text-[#000000] leading-snug group-hover:text-[#5FBE1F] transition-colors">
                  <a href={`/blog/${post.slug}`}>{post.title}</a>
                </h3>
                <p className="mt-3 text-sm text-[#4A4A46] leading-relaxed flex-1 line-clamp-3">
                  {post.excerpt}
                </p>
                <a
                  href={`/blog/${post.slug}`}
                  className="mt-5 inline-flex items-center gap-1 text-xs font-bold uppercase tracking-[0.18em] text-[#0A0A0A] hover:text-[#5FBE1F]"
                  data-testid={`home-blog-read-${post.slug}`}
                >
                  Read more <ArrowRight className="w-3.5 h-3.5" />
                </a>
              </div>
            </motion.article>
          ))}
        </div>

        <div className="mt-10 md:hidden">
          <a
            href="/blog"
            className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-[#0A0A0A] hover:text-[#5FBE1F]"
            data-testid="home-blog-view-all-mobile"
          >
            View all posts <ArrowRight className="w-3.5 h-3.5" />
          </a>
        </div>
      </div>
    </section>
  );
}
